import {
    Box,
    Container,
    Typography,
    Button,
    Stack,
    InputBase,
    Paper,
    useTheme,
} from '@mui/material';
import { Search } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import LandingImage from '../../../assets/banner2.jpg';
import { useState, useRef, useEffect } from 'react';
import { useTheme as useAppTheme } from '../../../contexts/ThemeContext';

const HeroSection = () => {
    const [trackingInput, setTrackingInput] = useState('');
    const navigate = useNavigate();
    const muiTheme = useTheme();
    const { mode } = useAppTheme();
    const inputRef = useRef<HTMLInputElement>(null);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setTrackingInput(e.target.value);
    };

    // Auto-focus the input when component mounts
    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus();
        }
    }, []);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const trimmed = trackingInput.trim();
        if (!trimmed) return;
        // Allow comma or space separated tracking numbers
        const numbers = trimmed.split(/[,\s]+/).filter(Boolean);
        if (numbers.length === 0) return;
        navigate(`/tracking?numbers=${encodeURIComponent(numbers.join(','))}`);
        setTrackingInput('');
    };

    // Dynamic background color based on theme
    const getBackgroundColor = () => {
        return mode === 'dark' ? '#111112' : '#f8f9fa';
    };

    // Dynamic text color based on theme
    const getTextColor = () => {
        return mode === 'dark' ? 'white' : muiTheme.palette.text.primary;
    };

    // Dynamic accent color based on theme
    const getAccentColor = () => {
        return mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)';
    };

    return (
        <Box sx={{ 
            minHeight: { xs: 'calc(100vh - 72px)', md: 'calc(100vh - 104px)' }, 
            bgcolor: getBackgroundColor(), 
            color: getTextColor(), 
            pt: 0, 
            display: 'flex', 
            alignItems: 'center' 
        }}>
            <Container maxWidth="xl" sx={{ flex: 1, display: 'flex', alignItems: 'center', height: '90vh', minHeight: '90vh', py: 0 }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={4} alignItems="center" justifyContent="space-between" sx={{ width: '100%' }}>
                    {/* Left Side: Text Content */}
                    <Box
                        component={motion.div}
                        initial={{ x: -180, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        transition={{ duration: 1.2, ease: [0.4, 0.1, 0.2, 1] }}
                        sx={{ flex: 1, zIndex: 2 }}
                    >
                        <Typography
                            variant="subtitle1"
                            sx={{
                                color: 'secondary.main',
                                letterSpacing: 6,
                                fontWeight: 700,
                                mb: 2,
                                textTransform: 'uppercase',
                                fontSize: { xs: 16, md: 18, lg: 20 },
                            }}
                        >
                            Delivering the Best
                        </Typography>
                        <Box sx={{ mb: 2 }}>
                            <Typography
                                component="span"
                                sx={{
                                    fontSize: { xs: 28, md: 36, lg: 48 },
                                    fontWeight: 400,
                                    color: getTextColor(),
                                    lineHeight: 1.1,
                                    display: 'block',
                                }}
                            >
                                <Box component="span" sx={{ color: 'secondary.main', fontWeight: 900, fontSize: { xs: 44, md: 64 }, verticalAlign: 'middle', mr: 1 }}>U</Box>
                                ltimate choice of contact between business and customers with most advanced ECOM Fulfillment
                            </Typography>
                        </Box>
                        <Typography
                            sx={{
                                color: mode === 'dark' ? 'grey.300' : 'grey.600',
                                fontSize: { xs: 14, md: 16, lg: 20 },
                                mb: 3,
                                borderLeft: '3px solid',
                                borderColor: 'secondary.main',
                                pl: 2,
                                maxWidth: 500,
                            }}
                        >
                            Young professionals of the industry, ready to serve the company's vision.
                        </Typography>
                        {/* Tracking Input */}
                        <Paper
                            component="form"
                            onSubmit={handleSubmit}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                width: { xs: '100%', md: 300, lg: 400 },
                                borderRadius: 2,
                                overflow: 'hidden',
                                boxShadow: 0,
                                bgcolor: 'background.paper',
                                transition: 'box-shadow 0.3s, border 0.3s',
                                border: '2px solid transparent',
                                '&:focus-within': {
                                    border: '2px solid',
                                    borderColor: 'secondary.main',
                                    boxShadow: '0 0 0 4px rgba(188,34,34,0.25)',
                                },
                            }}
                            elevation={0}
                        >
                            <InputBase
                                ref={inputRef}
                                value={trackingInput}
                                onChange={handleInputChange}
                                sx={{ ml: 2, flex: 1, fontSize: { xs: 14, md: 16, lg: 18 } }}
                                placeholder="Enter Tracking Number(s)"
                                inputProps={{ 'aria-label': 'Enter Tracking Number(s)' }}
                            />
                            <Button
                                type="submit"
                                sx={{
                                    bgcolor: 'secondary.main',
                                    color: 'white',
                                    minWidth: { xs: 36, md: 48, lg: 56 },
                                    height: { xs: 36, md: 48, lg: 56 },
                                    borderRadius: 0,
                                    '&:hover': { bgcolor: 'secondary.dark' },
                                }}
                            >
                                <Search sx={{ fontSize: 32 }} />
                            </Button>
                        </Paper>
                    </Box>
                    {/* Right Side: Image with Circle Accent */}
                    <Box
                        component={motion.div}
                        initial={{ x: 180, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        transition={{ duration: 1.2, ease: [0.4, 0.1, 0.2, 1] }}
                        sx={{
                            flex: 1,
                            display: { xs: 'none', md: 'flex' },
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'relative',
                            minHeight: 340,
                            mt: { xs: 6, md: 0 },
                        }}
                    >
                        <Box
                            sx={{
                                position: 'absolute',
                                left: '50%',
                                top: '50%',
                                transform: 'translate(-50%, -50%)',
                                width: { xs: 280, md: 320, lg: 420 },
                                height: { xs: 280, md: 320, lg: 420 },
                                borderRadius: '50%',
                                border: `2px solid ${mode === 'dark' ? '#222' : '#e0e0e0'}`,
                                boxShadow: `0 0 0 8px ${getAccentColor()}`,
                                zIndex: 1,
                                // Pulsing effect
                                '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    left: '50%',
                                    top: '50%',
                                    width: '100%',
                                    height: '100%',
                                    borderRadius: '50%',
                                    background: mode === 'dark' ? 'rgba(255, 255, 255, 0.18)' : 'rgba(0, 0, 0, 0.08)',
                                    transform: 'translate(-50%, -50%)',
                                    zIndex: -1,
                                    animation: 'pulse 2s infinite cubic-bezier(0.66,0,0,1)',
                                },
                                '@keyframes pulse': {
                                    '0%':   { transform: 'translate(-50%, -50%) scale(1.2)', opacity: 0.2 },
                                    '50%':  { transform: 'translate(-50%, -50%) scale(1.25)', opacity: 0.22 },
                                    '100%': { transform: 'translate(-50%, -50%) scale(1.2)', opacity: 0.2 },
                                },
                            }}
                        />
                        <Box
                            component="img"
                            src={LandingImage}
                            alt="Courier Hero"
                            sx={{
                                width: { xs: 280, md: 320, lg: 420 },
                                height: { xs: 280, md: 320, lg: 420 },
                                objectFit: 'cover',
                                borderRadius: '50%',
                                position: 'relative',
                                zIndex: 2,
                                boxShadow: mode === 'dark' 
                                    ? '0 8px 32px 0 rgba(0,0,0,0.25)' 
                                    : '0 8px 32px 0 rgba(0,0,0,0.15)',
                            }}
                        />
                    </Box>
                </Stack>
            </Container>
        </Box>
    );
};

export default HeroSection; 